import React, { useState } from 'react';
import { CheckCircle2, XCircle, Info, ChevronDown, Hash, AlertTriangle, Lightbulb } from 'lucide-react';
import { NgapAct } from '../../types/ngap';
import { generateHashtagUrl } from '../../utils/urlUtils';

interface ActDetailsProps {
  act: NgapAct;
  darkMode: boolean;
  onHashtagClick?: (hashtag: string) => void;
}

const ActDetails: React.FC<ActDetailsProps> = ({
  act,
  darkMode
}) => {
  const [isExtraOpen, setIsExtraOpen] = useState(false);

  const parsePathologies = React.useMemo(() => {
    if (!act.pathologies || !act.pathologies.trim()) return [];
    return act.pathologies
      .split(',')
      .map(p => p.trim())
      .filter(p => p.length > 0);
  }, [act.pathologies]);

  const handleHashtagClick = (hashtag: string) => {
    window.location.href = generateHashtagUrl(hashtag);
  };

  const hasReferentielInfo = act.soumis_referentiel && (act.number_seances || act.demande_accord_prealable);
  const hasExtraInfo = Boolean(act.tips) || Boolean(act.conditions) || hasReferentielInfo;

  const isOpere = act.vignette_label_4?.toLowerCase() === 'opéré';
  const isNonOpere = act.vignette_label_4?.toLowerCase() === 'non-opéré';

  const mutedText = darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65';
  const bodyText = darkMode ? 'text-appines-white/65' : 'text-appines-petrol/65';
  const chipClass = darkMode
    ? 'bg-appines-white/10 text-appines-white/80 hover:bg-appines-white/15 border-appines-white/20'
    : 'bg-[rgba(1,42,54,0.04)] text-appines-petrol hover:bg-[rgba(1,42,54,0.08)] border-[rgba(1,42,54,0.12)]';

  return (
    <div className="space-y-3">
      {parsePathologies.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {parsePathologies.map((pathology, index) => (
            <button
              key={index}
              onClick={() => handleHashtagClick(pathology)}
              className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full border transition-colors ${chipClass}`}
              aria-label={`Rechercher la pathologie ${pathology}`}
            >
              <Hash className="h-3 w-3 mr-0.5 opacity-60" />
              {pathology}
            </button>
          ))}
        </div>
      )}

      <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-xs ${bodyText}`}>
        {act.vignette_label_4 && (isOpere || isNonOpere) && (
          <div className="flex items-center gap-1">
            {isOpere ? (
              <CheckCircle2 className="h-3.5 w-3.5 text-appines-emerald" aria-hidden="true" />
            ) : (
              <XCircle className={`h-3.5 w-3.5 ${mutedText}`} aria-hidden="true" />
            )}
            <span>{act.vignette_label_4}</span>
          </div>
        )}

        {act.soumis_referentiel !== undefined && (
          <div className="flex items-center gap-1" role="status">
            {act.soumis_referentiel ? (
              <CheckCircle2 className="h-3.5 w-3.5 text-appines-emerald" aria-hidden="true" />
            ) : (
              <XCircle className={`h-3.5 w-3.5 ${mutedText}`} aria-hidden="true" />
            )}
            <span>{act.soumis_referentiel ? 'Référentiel' : 'Hors référentiel'}</span>
          </div>
        )}

        {act.old_lettre_cle && act.old_cotation_valeur && (
          <div className={`flex items-center gap-1 ${mutedText}`}>
            <span>Ancien : {act.old_lettre_cle} {act.old_cotation_valeur}</span>
          </div>
        )}
      </div>

      {hasExtraInfo && (
        <div className={`rounded-md border ${
          darkMode ? 'border-appines-white/12' : 'border-[rgba(1,42,54,0.12)]'
        }`}>
          <button
            onClick={() => setIsExtraOpen(!isExtraOpen)}
            className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium transition-colors ${
              darkMode
                ? 'text-appines-white/80 hover:bg-appines-white/5'
                : 'text-appines-petrol hover:bg-[rgba(1,42,54,0.04)]'
            }`}
            aria-expanded={isExtraOpen}
          >
            <span className="flex items-center gap-2">
              <Info className={`h-4 w-4 ${mutedText}`} aria-hidden="true" />
              Plus d'infos
            </span>
            <ChevronDown
              className={`h-4 w-4 ${mutedText} transition-transform ${
                isExtraOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {isExtraOpen && (
            <div className={`px-3 pb-3 pt-1 space-y-3 text-sm ${bodyText}`}>
              {hasReferentielInfo && (
                <div className="space-y-1">
                  <p className={`text-xs font-semibold uppercase tracking-wide ${mutedText}`}>
                    Référentiel
                  </p>
                  {act.number_seances && (
                    <p>
                      <span className="font-medium">Séances avant accord :</span>{' '}
                      {act.number_seances}
                    </p>
                  )}
                  {act.demande_accord_prealable && (
                    <p>
                      <span className="font-medium">Accord préalable :</span>{' '}
                      {act.demande_accord_prealable}
                    </p>
                  )}
                </div>
              )}

              {act.conditions && (
                <div className="space-y-1">
                  <p className={`text-xs font-semibold uppercase tracking-wide flex items-center gap-1 ${mutedText}`}>
                    <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                    Conditions
                  </p>
                  <p>{act.conditions}</p>
                </div>
              )}

              {act.tips && (
                <div className="space-y-1">
                  <p className={`text-xs font-semibold uppercase tracking-wide flex items-center gap-1 ${mutedText}`}>
                    <Lightbulb className="h-3.5 w-3.5" aria-hidden="true" />
                    Conseils
                  </p>
                  <p>{act.tips}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ActDetails;
